import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '../api/client'
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, DownloadIcon, TrashIcon } from '../components/Icons'
import type { ActivityDetail, ExerciseType, Trackpoint } from '../api/types'
import { EXERCISE_TYPES } from '../api/types'
import { RouteMap } from '../components/RouteMap'
import { TrackCharts } from '../components/TrackCharts'
import { dayAndTime, distanceKm, duration, fileStamp, integer, pace, paceFromSpeed, timeOnly } from '../format'
import { useAuth } from '../auth'

export function RunPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const [detail, setDetail] = useState<ActivityDetail | null>(null)
  const [points, setPoints] = useState<Trackpoint[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [exerciseType, setExerciseType] = useState<ExerciseType>("RUNNING")

  const handleFailure = useCallback(
    (failure: unknown) => {
      if (failure instanceof ApiError && failure.isUnauthorised) {
        signOut()
        return
      }
      setError(failure instanceof Error ? failure.message : 'Something went wrong')
    },
    [signOut],
  )

  useEffect(() => {
    let cancelled = false
    setDetail(null)
    setPoints(null)
    setError(null)
    Promise.all([api.activity(id), api.track(id)])
      .then(([activity, track]) => {
        if (cancelled) return
        setDetail(activity)
        setNotes(activity.summary.notes ?? '')
        setExerciseType(activity.summary.exerciseType)
        setPoints(track)
      })
      .catch((failure: unknown) => {
        if (!cancelled) handleFailure(failure)
      })
    return () => {
      cancelled = true
    }
  }, [id, handleFailure])

  async function saveNotes() {
    setSavingNotes(true)
    try {
      await api.updateNotes(id, notes)
    } catch (failure) {
      handleFailure(failure)
    } finally {
      setSavingNotes(false)
    }
  }

  /** Reclassifies the run. Saves on change: there is nothing else to fill in alongside it. */
  async function changeExerciseType(next: ExerciseType) {
    const previous = exerciseType
    setExerciseType(next)
    try {
      await api.updateExerciseType(id, next)
    } catch (failure) {
      setExerciseType(previous)
      handleFailure(failure)
    }
  }

  async function remove() {
    if (!window.confirm('Delete this activity? The stored TCX file goes with it.')) return
    try {
      await api.deleteActivity(id)
      navigate('/')
    } catch (failure) {
      handleFailure(failure)
    }
  }

  if (error && !detail) {
    return (
      <div className="page">
        <div className="error">{error}</div>
        <p>
          <Link to="/">← Back to your activities</Link>
        </p>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="page">
        <div className="spinner">Loading the run…</div>
      </div>
    )
  }

  const run = detail.summary

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link to="/" className="muted">
            ← All activities
          </Link>
          <h1 style={{ marginTop: 6 }}>{dayAndTime(run.startTime)}</h1>
          {/* Built from a filtered list so a missing part cannot leave a stray separator. */}
          <div className="muted">
            {[
              run.creatorName,
              run.endTime ? `finished ${timeOnly(run.endTime)}` : null,
              `${detail.trackpointCount} trackpoints`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <label htmlFor="exerciseType" className="inline-field">
            <span>Type</span>
            <select
              id="exerciseType"
              value={exerciseType}
              onChange={(e) => void changeExerciseType(e.target.value as ExerciseType)}
            >
              {EXERCISE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {/* Icon-only paging, sat with the other actions. Newer on the left, older on the
              right, matching the direction the dashboard list runs. Disabled rather than hidden
              at the ends, so the row does not reflow as you page to the first or last. */}
          <button
            type="button"
            className="icon-button"
            title="Newer"
            aria-label="Newer activity"
            disabled={!detail.newer}
            onClick={() => detail.newer && navigate(`/runs/${detail.newer.id}`)}
          >
            <ArrowLeftIcon />
          </button>
          <button
            type="button"
            className="icon-button"
            title="Older"
            aria-label="Older activity"
            disabled={!detail.older}
            onClick={() => detail.older && navigate(`/runs/${detail.older.id}`)}
          >
            <ArrowRightIcon />
          </button>
          <button
            type="button"
            onClick={() => {
              void api
                .downloadTcx(id, `mytracker-${fileStamp(run.startTime)}.tcx`)
                .catch(handleFailure)
            }}
          >
            <DownloadIcon />
            Download TCX
          </button>
          <button type="button" onClick={() => void remove()}>
            <TrashIcon />
            Delete
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="tiles" style={{ marginBottom: 16 }}>
        <Tile label="Distance" value={distanceKm(run.distanceMeters)} unit="km" />
        <Tile label="Moving time" value={duration(run.totalTimeSeconds)} />
        <Tile label="Average pace" value={pace(run.avgPaceSecondsPerKm)} unit="/km" />
        <Tile label="Best pace" value={paceFromSpeed(run.maxSpeedMps)} unit="/km" />
        <Tile label="Average HR" value={integer(run.avgHeartRateBpm)} unit="bpm" />
        <Tile label="Max HR" value={integer(run.maxHeartRateBpm)} unit="bpm" />
        <Tile
          label="Cadence"
          value={run.avgCadence === undefined ? '—' : String(run.avgCadence * 2)}
          unit="spm"
        />
        <Tile label="Calories" value={integer(run.calories)} unit="kcal" />
        <Tile label="Ascent" value={integer(run.totalAscentMeters)} unit="m" />
        <Tile label="Descent" value={integer(run.totalDescentMeters)} unit="m" />
      </div>

      <div className="detail-grid">
        <div>{points === null ? <div className="map-empty">Loading the route…</div> : <RouteMap points={points} />}</div>
        <div>{points && <TrackCharts points={points} />}</div>
      </div>

      <h2>Splits</h2>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Lap</th>
              <th>Distance</th>
              <th>Time</th>
              <th>Pace</th>
              <th>Avg HR</th>
              <th>Max HR</th>
              <th>Cadence</th>
              <th>kcal</th>
              <th>Trigger</th>
            </tr>
          </thead>
          <tbody>
            {detail.laps.map((lap) => (
              <tr key={lap.id}>
                <td>{lap.lapIndex + 1}</td>
                <td>{distanceKm(lap.distanceMeters)} km</td>
                <td>{duration(lap.totalTimeSeconds)}</td>
                <td>{pace(lap.avgPaceSecondsPerKm)}</td>
                <td>{integer(lap.averageHeartRateBpm)}</td>
                <td>{integer(lap.maximumHeartRateBpm)}</td>
                <td>{lap.avgRunCadence === undefined ? '—' : lap.avgRunCadence * 2}</td>
                <td>{integer(lap.calories)}</td>
                <td>
                  <span className="pill">{lap.triggerMethod ?? 'Manual'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Notes</h2>
      <div className="card" style={{ padding: 16 }}>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="How did it feel?"
        />
        <div className="row-actions">
          <button type="button" onClick={() => void saveNotes()} disabled={savingNotes}>
            <CheckIcon />
            {savingNotes ? 'Saving…' : 'Save notes'}
          </button>
        </div>
      </div>

      <h2>File</h2>
      <div className="card" style={{ padding: 16 }}>
        <table>
          <tbody>
            <FileRow label="TCX activity id" value={run.tcxActivityId} />
            <FileRow label="Recorded by" value={run.creatorName} />
            <FileRow label="Device version" value={detail.creatorVersion} />
            <FileRow label="Written by" value={detail.authorName} />
            <FileRow label="Writer build" value={detail.authorBuild} />
            <FileRow label="Uploaded" value={dayAndTime(run.uploadedAt)} />
          </tbody>
        </table>
        <p className="muted" style={{ marginBottom: 0, marginTop: 12, fontSize: 13 }}>
          The original file is stored exactly as it was uploaded — Download TCX returns those same
          bytes, so nothing is lost by keeping your activities here.
        </p>
      </div>
    </div>
  )
}

function Tile({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="tile">
      <div className="tile-label">{label}</div>
      <div className="tile-value">
        {value}
        {unit && value !== '—' && <span className="tile-unit">{unit}</span>}
      </div>
    </div>
  )
}

function FileRow({ label, value }: { label: string; value?: string }) {
  return (
    <tr>
      <td className="muted">{label}</td>
      <td>{value ?? '—'}</td>
    </tr>
  )
}
